/**
 * Orchestration: the unified approval inbox, instance timelines, and templates.
 *
 * Every approvable thing in the product — timesheets, leave, expenses, POs, payroll,
 * salary changes — raises a workflow instance. `GET /api/workflows/pending/mine` is
 * therefore the ONE queue; there is no separate "leave approvals" list to fall out of
 * sync with it. Module-specific queues (e.g. `/api/timesheets/pending`) still exist and
 * are used where a specialised view earns its keep, but this is the default.
 */
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import { api } from '@frontend/api/client';

export interface PendingApproval {
  instanceId: string;
  stepInstanceId: string;
  processKey: string;
  entityType: string;
  entityId: string;
  stepName: string;
  requestedByUserId: string;
  requestedAt: string;
  dueAt: string | null;
  isOverdue: boolean;
  escalatedAt: string | null;
  approvalCount: number;
  minApprovals: number;
  summary: string;
}

export function usePendingApprovals(): UseQueryResult<{ items: PendingApproval[] }> {
  return useQuery({
    queryKey: ['workflows', 'pending'],
    queryFn: () => api.get<{ items: PendingApproval[] }>('/workflows/pending/mine'),
    staleTime: 20_000,
    // A user with no approval rights gets a 403 here. That is not an error worth
    // surfacing in the shell — the inbox nav item is simply hidden.
    retry: false,
  });
}

export interface WorkflowStepView {
  id: string;
  key: string;
  name: string;
  type: string;
  status: string;
  assignedUserIds?: string[];
  dueAt?: string | null;
  startedAt?: string | null;
  completedAt?: string | null;
  decision?: string | null;
  decidedByUserId?: string | null;
  comment?: string | null;
  sortOrder?: number;
}

export interface WorkflowInstanceView {
  instance: {
    id: string;
    processKey: string;
    entityType: string;
    entityId: string;
    status: string;
    requestedByUserId: string;
    requestedAt: string;
    completedAt: string | null;
    currentStepKey?: string | null;
    context?: Record<string, unknown>;
  };
  steps: WorkflowStepView[];
  summary?: string;
}

export function useWorkflowInstance(id: string | null): UseQueryResult<WorkflowInstanceView> {
  return useQuery({
    queryKey: ['workflows', 'instance', id],
    queryFn: () => api.get<WorkflowInstanceView>(`/workflows/${id ?? ''}`),
    enabled: id !== null && id !== '',
  });
}

export type WorkflowDecision = 'APPROVED' | 'REJECTED' | 'DELEGATED' | 'REQUESTED_CHANGES';

export interface DecideInput {
  instanceId: string;
  stepInstanceId: string;
  decision: WorkflowDecision;
  comment?: string | null;
  delegateToUserId?: string | null;
}

export function useDecideStep(): ReturnType<typeof useMutation<unknown, unknown, DecideInput>> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: DecideInput) =>
      api.post(`/workflows/${input.instanceId}/steps/${input.stepInstanceId}/decide`, {
        decision: input.decision,
        comment: input.comment ?? null,
        delegateToUserId: input.delegateToUserId ?? null,
      }),
    /**
     * Optimistic removal from the queue. An approver clicking through thirty timesheets
     * should not wait for a round trip between each one; on failure the queue is
     * refetched and the item comes back with the server's error shown as a toast.
     */
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey: ['workflows', 'pending'] });
      const previous = queryClient.getQueryData<{ items: PendingApproval[] }>(['workflows', 'pending']);
      queryClient.setQueryData<{ items: PendingApproval[] }>(['workflows', 'pending'], (current) =>
        current === undefined
          ? current
          : { items: current.items.filter((item) => item.stepInstanceId !== input.stepInstanceId) },
      );
      return { previous };
    },
    onError: (_error, _input, context) => {
      const previous = (context as { previous?: { items: PendingApproval[] } } | undefined)?.previous;
      if (previous !== undefined) queryClient.setQueryData(['workflows', 'pending'], previous);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['workflows'] });
      // The underlying record's own list (timesheets, leave, expenses) is now stale too.
      void queryClient.invalidateQueries({ queryKey: ['timesheets'] });
      void queryClient.invalidateQueries({ queryKey: ['leave'] });
      void queryClient.invalidateQueries({ queryKey: ['expenses'] });
    },
  });
}

// ── Templates ────────────────────────────────────────────────────────────────

export interface WorkflowTemplateSummary {
  id: string;
  processKey: string;
  name: string;
  version: number;
  status?: string;
  isActive?: boolean;
  isPublished?: boolean;
  updatedAt?: string;
  description?: string | null;
}

/**
 * `includeInactive` matters on the admin screen and nowhere else.
 *
 * The route filters to `isActive: true` by default, and "inactive" here means UNPUBLISHED —
 * so an admin who saved a draft and came back found it missing from the list with no
 * indication it had been kept. Everywhere else the default is right: a draft template
 * governs nothing and should not be offered as if it did.
 */
export function useWorkflowTemplates(options?: {
  includeInactive?: boolean;
}): UseQueryResult<{ items: WorkflowTemplateSummary[] }> {
  const includeInactive = options?.includeInactive === true;
  return useQuery({
    queryKey: ['workflows', 'templates', includeInactive],
    queryFn: () =>
      api.get<{ items: WorkflowTemplateSummary[] }>('/workflow-templates', {
        includeInactive: includeInactive ? 'true' : undefined,
      }),
  });
}

export interface WorkflowTemplateDetail extends WorkflowTemplateSummary {
  steps: {
    id: string;
    key: string;
    name: string;
    type: string;
    sortOrder: number;
    slaHours?: number | null;
    trueBranchStepId?: string | null;
    falseBranchStepId?: string | null;
    assigneeType?: string | null;
  }[];
}

export function useWorkflowTemplate(id: string | null): UseQueryResult<WorkflowTemplateDetail> {
  return useQuery({
    queryKey: ['workflows', 'template', id],
    queryFn: () => api.get<WorkflowTemplateDetail>(`/workflow-templates/${id ?? ''}`),
    enabled: id !== null && id !== '',
  });
}

export interface StuckInstance {
  instanceId: string;
  processKey: string;
  entityType: string;
  entityId: string;
  status: string;
  requestedByUserId: string;
  requestedAt: string;
  ageHours: number;
  reason: string;
}

export function useStuckWorkflows(olderThanHours?: number): UseQueryResult<{ items: StuckInstance[] }> {
  return useQuery({
    queryKey: ['workflows', 'stuck', olderThanHours ?? 0],
    queryFn: () => api.get<{ items: StuckInstance[] }>('/workflows/stuck', { olderThanHours }),
    retry: false,
  });
}
